import { t } from 'i18next';
import React, { FC, useCallback, useEffect, useState } from 'react';

import { AudienceScope, EntityType } from 'Features/audience/pages/AudienceGroupList/types';
import { AudiencePlayerList } from 'Features/audience/pages/AudiencePlayerList/AudiencePlayerList';

import { SCodeInput } from 'Components/structural';
import { getRandomString } from 'Libs/randomize/randomizer';
import { useAppDispatch } from 'Libs/redux/store';
import { useTypedSelector } from 'Libs/redux/utils';
import {
  selectMagicCodeExistance,
  selectMagicCodeExistanceMetadata,
} from 'Store/entities/configuration/magic-codes/magic-codes.selectors';
import { fetchMagicCodes } from 'Store/entities/configuration/magic-codes/magic-codes.thunks';

import styles from './AudienceMagicCode.style';

const COPY_TIME = 1000;

export type AudienceMagicCodeProps = {
  entityType: EntityType;
  entityId: number;
  originalValue?: string;
  value?: string;
  update: (value: string) => void;
  scope: AudienceScope;
  hasAudienceTypeChanged: boolean;
  loading?: boolean;
};

export const AudienceMagicCode: FC<AudienceMagicCodeProps> = ({
  entityId,
  entityType,
  originalValue,
  value,
  update,
  scope,
  hasAudienceTypeChanged,
  loading: loadingFromProps = false,
}) => {
  const activityName =
    scope === 'campaign' ? t('activities:audience.campaign_scope') : t('activities:audience.breaking_news_scope');

  const dispatch = useAppDispatch();

  const [buttonText, setButtonText] = useState<string>(t('activities:audience.magic_code.copy'));
  const [error, setError] = useState({ status: 'none', message: '' });

  const magicCodeExists = useTypedSelector(selectMagicCodeExistance(value || ''));
  const { loading } = useTypedSelector(selectMagicCodeExistanceMetadata);

  useEffect(() => {
    dispatch(fetchMagicCodes());

    if (!value) {
      update(getRandomString(6));
    }
  }, []);

  useEffect(() => {
    if (magicCodeExists && value && originalValue !== value) {
      setError({ status: 'warning', message: t('activities:audience.magic_code.already_used') });
      return;
    }
    setError({ status: 'none', message: '' });
  }, [magicCodeExists, originalValue]);

  const onIconClick = () => {
    update(getRandomString(6));
  };
  const onButtonClick = useCallback(() => {
    navigator.clipboard.writeText(value || '');

    setButtonText(t('activities:audience.magic_code.copied'));
    setTimeout(() => setButtonText(t('activities:audience.magic_code.copy')), COPY_TIME);
  }, []);

  const onInputChange = (value: string) => {
    update(value);
  };

  return (
    <div style={styles.infoWrapper}>
      <div style={styles.title}>{t('activities:audience.magic_code.title', { activity: activityName })}</div>
      <div style={styles.subtitle}>{t('activities:audience.magic_code.subtitle', { activity: activityName })}</div>
      <SCodeInput
        value={value || ''}
        onButtonClick={onButtonClick}
        onIconClick={onIconClick}
        onInputChange={onInputChange}
        buttonText={buttonText}
        style={styles.codeInput}
        description={t('activities:audience.magic_code.description')}
        codeChangeFeedbackStatus={error.status}
        codeChangeFeedbackMessage={error.message}
        loading={loadingFromProps || loading}
      />
      <div style={styles.audienceTable}>
        <AudiencePlayerList
          entityId={entityId}
          entityType={entityType}
          scope={scope}
          audienceType="magic-code"
          audienceHasBeenReset={hasAudienceTypeChanged}
          loading={loadingFromProps || loading}
        />
      </div>
    </div>
  );
};
